import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box, Button, Grid, TextField } from '@mui/material';
import { BlockVariables } from '../../utils/models/blocks';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';


interface VariableInputParamsProps {
    open: boolean,
    inputVariables: BlockVariables[],
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    handleModalSubmit: (formData: { [key: string]: string | number }) => void,
}


const VariableInputModal: React.FC<VariableInputParamsProps> = ({
    open,
    inputVariables,
    setOpen,
    handleModalSubmit,
}) => {

    const { control, handleSubmit, reset, formState: { errors } } = useForm<{ [key: string]: string | number }>();

    const handleClose = () => {
        setOpen(false);
    };

    const handleInputParamsSubmit: SubmitHandler<{ [key: string]: string | number }> = (formData) => {
        // console.log("form data: ", formData)
        handleModalSubmit(formData)
        reset();
        handleClose();
    }


    return (
        <Dialog
            open={open}
            component='form'
            onClose={handleClose}
            aria-labelledby="variable-input-dialog"
            onSubmit={handleSubmit(handleInputParamsSubmit)}
            sx={{
                '& .MuiDialog-paperWidthSm': {
                    minWidth: '600px',
                    maxWidth: "min-content",
                    minHeight: "500px",
                    maxHeight: '75%',
                },
            }}
        >
            <DialogTitle
                id="variable-input-dialog-title"
                sx={{
                    m: 0,
                    pt: 1.5,
                    pb: 1,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Typography variant='h6' component='p'>Input Parameters</Typography>
                    <Typography variant='subtitle2' component='p'>Provide input param values</Typography>
                </Box>

                <IconButton
                    aria-label="close"
                    type='button'
                    onClick={handleClose}
                    sx={{ width: 45, height: 45 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    {inputVariables.map((variable, index) => (
                        <Grid item xs={12} key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ width: 'fit-content' }}>
                                <Typography component='p'>{variable.name}</Typography>
                                <Typography variant='caption'>Type: {variable.type.toLowerCase()}</Typography>
                            </Box>

                            <Box sx={{ display: "flex" }}>
                                <Box sx={{ margin: "auto 24px" }}>=</Box>

                                <Controller
                                    name={variable.name}
                                    control={control}
                                    defaultValue=""
                                    rules={{
                                        required: 'Required field',
                                    }}
                                    render={({ field }) => {
                                        const fieldError = errors[variable.name];
                                        return <TextField
                                            {...field}
                                            variant='outlined'
                                            type={variable.type === 'Number' ? 'number' : 'text'}
                                            error={!!fieldError}
                                            helperText={fieldError && fieldError?.message}
                                            autoComplete='off'
                                            placeholder={variable.name}
                                            sx={{ minWidth: "250px" }}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                field.onChange(variable.type === 'Number' ? Number(value) : value);
                                            }}
                                        />
                                    }}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button type='submit' variant="contained" sx={{ width: 120 }}>
                    run test
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default VariableInputModal